import { type FunctionComponent, type ReactElement, useContext } from 'react';
import { MediaGalleryContext } from '../../../contexts/pdp/media-gallery.context';
import { type Media } from '../../../helpers/images/images.helper';
import { useClientOnlyBreakpoints } from '../../../hooks/breakpoints/client-only-breakpoints.hook';
import { DEFAULT_CARD_WIDTH, useUpdateMediaProduct } from '../../../hooks/pdp/media-gallery/media-gallery.hooks';
import { ImageGalleryCarousel } from '../../carousel-components/image-gallery-carousel/image-gallery-carousel.component';
import { ThumbnailCard } from '../../carousel-components/thumbnail-carousel/thumbnail-components/thumbnail-components.component';
import { Modal } from '../../common-components/modal/modal.component';

export type GalleryThumbnailsProps = {
	items: Media[];
	title?: string;
	currentThumbnail: Media;
	setCurrentThumbnail: (media: Media) => void;
};

const GalleryThumbnails: FunctionComponent<GalleryThumbnailsProps> = ({ items, currentThumbnail, setCurrentThumbnail }) => {
	const { slidesPerView } = useContext(MediaGalleryContext);
	const { updateCarouselSlides } = useUpdateMediaProduct(items);

	return (
		<div className="dn flex-ns flex-wrap max-vh-75-ns overflow-y-auto-ns">
			{items.map((item, index) => {
				return (
					<button
						key={index}
						className={`ba br2 bw1 hover-b--theme-primary pointer outline-0 dib ml2 mt2 bg-theme-white ${
							item.id === currentThumbnail.id ? 'b--theme-primary' : 'b--theme-grey-light'
						}`}
						style={{ height: '102px', width: '102px' }}
						tabIndex={0}
						onClick={() => {
							updateCarouselSlides(index, DEFAULT_CARD_WIDTH, slidesPerView);
							setCurrentThumbnail(item);
						}}
						data-testid={`gallery-thumbnail-${index}`}>
						<ThumbnailCard
							item={item}
							ariaLabel="thumbnails"
							className="pa0"
							imageOptions={{ options: { width: 80, height: 80 } }}
						/>
					</button>
				);
			})}
		</div>
	);
};

export type MediaGalleryModalProps = {
	items: Media[];
	triggerElement?: ReactElement;
	title?: string;
	modalId?: string;
	onClose?: () => void;
	pageName?: string;
	currentMediaImage: Media;
	onSelectedMediaChange: (item: Media) => void;
	shouldOpenVideoInModal: boolean;
	showModal?: boolean;
};
export const MediaGalleryModal: FunctionComponent<MediaGalleryModalProps> = ({
	triggerElement,
	items,
	title,
	modalId,
	onClose,
	currentMediaImage,
	onSelectedMediaChange,
	shouldOpenVideoInModal
}) => {
	const { isNotSmall, isLarge } = useClientOnlyBreakpoints();

	const smallDeviceSizesByPage = { width: 330, height: 320 };
	const mediumDeviceSizesByPage = { height: 450, width: 450 };
	const largeDeviceSizesByPage = { height: 680, width: 680 };

	let deviceSizes = smallDeviceSizesByPage;
	if (isNotSmall) {
		deviceSizes = isLarge ? largeDeviceSizesByPage : mediumDeviceSizesByPage;
	}

	if (!isNotSmall) {
		onClose?.();
		return null;
	}

	return (
		<Modal
			title={title}
			modalId={modalId}
			ariaLabel="Media Gallery"
			triggerElement={triggerElement}
			size="stretch"
			closeAction={onClose}
			restoreFocus={false}>
			<div className="flex flex-wrap">
				<div className="w-100 w-60-ns">
					<ImageGalleryCarousel
						items={items}
						thumbnailMode={'hide'}
						selectedItemId={currentMediaImage?.id}
						onSelectedImageChange={onSelectedMediaChange}
						galleryImageOptions={{ options: deviceSizes, bypassLazyLoad: false }}
						videoStyling="ph2 pv7"
						isInModal={true}
						shouldOpenVideoInModal={shouldOpenVideoInModal}
					/>
				</div>
				<div className="w-100 w-40-ns mt5 mt0-ns">
					<GalleryThumbnails
						items={items}
						title={title}
						currentThumbnail={currentMediaImage}
						setCurrentThumbnail={onSelectedMediaChange}
					/>
				</div>
			</div>
		</Modal>
	);
};
